import React, { useEffect, useState } from 'react';
import { ImageControlsProps } from '../utils/types';
import ImageMetadata from './ImageMetadata';

const formatMimeTypes = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  bmp: 'image/bmp',
  avif: 'image/avif',
};

const formatLabels = [
  { value: 'jpg', label: 'JPG' },
  { value: 'jpeg', label: 'JPEG' },
  { value: 'png', label: 'PNG' },
  { value: 'webp', label: 'WebP' },
  { value: 'bmp', label: 'BMP' },
  { value: 'avif', label: 'AVIF' },
];

const ImageControls: React.FC<ImageControlsProps> = ({
  operationMode,
  imgSrc,
  processedImage,
  processedSize,
  onProcess,
  onDownload,
  onReset,
  cropProps,
  compressProps,
  metadata,
}) => {
  const [supportedFormats, setSupportedFormats] = useState<{ [key: string]: boolean }>({});
  useEffect(() => {
    const testFormats = async () => {
      const results: { [key: string]: boolean } = { jpg: true, jpeg: true, png: true };
      for (const fmt of ['webp', 'bmp', 'avif']) {
        try {
          const mime = (formatMimeTypes as any)[fmt];
          const can = document.createElement('canvas');
          const ok = can.toDataURL(mime).indexOf(`data:${mime}`) === 0;
          results[fmt] = ok;
        } catch {
          results[fmt] = false;
        }
      }
      setSupportedFormats(results);
    };
    testFormats();
  }, []);

  if (!imgSrc) return null;

  // Use quality and setQuality from compressProps for the slider, fallback to 0.8 if not present
  const quality = compressProps?.quality ?? 0.8;
  const setQuality = compressProps?.setQuality ?? (() => {});
  const format = compressProps?.format ?? 'jpg';
  const setFormat = compressProps?.setFormat ?? (() => {});
  const outputWidth = compressProps?.outputWidth ?? '';
  const setOutputWidth = compressProps?.setOutputWidth ?? (() => {});
  const outputHeight = compressProps?.outputHeight ?? '';
  const setOutputHeight = compressProps?.setOutputHeight ?? (() => {});
  const lockAspectRatio = compressProps?.lockAspectRatio ?? false;
  const setLockAspectRatio = compressProps?.setLockAspectRatio ?? (() => {});

  // Calculate aspect ratio from metadata or crop
  let aspectRatio: number | undefined = undefined;
  if (lockAspectRatio) {
    if (cropProps?.completedCrop && cropProps.completedCrop.width && cropProps.completedCrop.height) {
      aspectRatio = cropProps.completedCrop.width / cropProps.completedCrop.height;
    } else if (metadata && metadata.width && metadata.height) {
      aspectRatio = metadata.width / metadata.height;
    }
  }

  // Handlers for width/height input with aspect ratio lock
  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value));
    setOutputWidth(value);
    if (lockAspectRatio && aspectRatio && value !== '') {
      setOutputHeight(Math.round(value / aspectRatio));
    }
  };
  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value));
    setOutputHeight(value);
    if (lockAspectRatio && aspectRatio && value !== '') {
      setOutputWidth(Math.round(value * aspectRatio));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Metadata Display */}
      <ImageMetadata metadata={metadata} />

      {/* Controls */}
      <div className="flex flex-wrap gap-4 justify-center items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        {cropProps ? (
          <>
            {/* Aspect Ratio Controls */}
            <div className="flex gap-2">
              <button
                onClick={() => cropProps.setAspect(undefined)}
                className={`px-3 py-1 rounded-md transition-colors ${
                  cropProps.aspect === undefined
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                Free
              </button>
              <button
                onClick={() => cropProps.setAspect(16 / 9)}
                className={`px-3 py-1 rounded-md transition-colors ${
                  cropProps.aspect === 16 / 9
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                16:9
              </button>
              <button
                onClick={() => cropProps.setAspect(4 / 3)}
                className={`px-3 py-1 rounded-md transition-colors ${
                  cropProps.aspect === 4 / 3
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                4:3
              </button>
              <button
                onClick={() => cropProps.setAspect(1)}
                className={`px-3 py-1 rounded-md transition-colors ${
                  cropProps.aspect === 1
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
              >
                1:1
              </button>
            </div>

            {/* Scale Control */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 dark:text-gray-300">Scale:</label>
              <input
                type="range"
                min="0.1"
                max="2"
                step="0.1"
                value={cropProps.scale}
                onChange={(e) => cropProps.setScale(Number(e.target.value))}
                className="w-24"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {cropProps.scale.toFixed(1)}x
              </span>
            </div>
          </>
        ) : null}

        {/* Quality and Format Controls */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Quality Control */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Quality:</label>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.01"
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {Math.round(quality * 100)}%
            </span>
          </div>
          {/* Format Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Format:</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
            >
              {formatLabels.map(({ value, label }) => (
                <option
                  key={value}
                  value={value}
                  disabled={supportedFormats[value] === false}
                >
                  {label}
                  {supportedFormats[value] === false ? ' (not supported)' : ''}
                </option>
              ))}
            </select>
          </div>
          {/* Resize Controls */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Width:</label>
            <input
              type="number"
              min={1}
              value={outputWidth}
              onChange={handleWidthChange}
              className="w-20 px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
              placeholder={metadata?.width?.toString() || ''}
            />
            <label className="text-sm text-gray-700 dark:text-gray-300">px</label>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 dark:text-gray-300">Height:</label>
            <input
              type="number"
              min={1}
              value={outputHeight}
              onChange={handleHeightChange}
              className="w-20 px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
              placeholder={metadata?.height?.toString() || ''}
            />
            <label className="text-sm text-gray-700 dark:text-gray-300">px</label>
            {/* Reset button for dimensions */}
            <button
              type="button"
              onClick={() => { setOutputWidth(''); setOutputHeight(''); }}
              className="ml-2 px-2 py-1 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              Reset
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={lockAspectRatio}
              onChange={(e) => setLockAspectRatio(e.target.checked)}
              id="lock-aspect-ratio"
            />
            <label htmlFor="lock-aspect-ratio" className="text-sm text-gray-700 dark:text-gray-300">Lock Aspect Ratio</label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={onProcess}
            disabled={cropProps?.completedCrop === undefined}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Crop/Compress
          </button>
          {processedImage && (
            <button
              onClick={onDownload}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
            >
              Download
            </button>
          )}
          <button
            onClick={onReset}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageControls; 